import { money, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { format } from "date-fns";
import { FileText, Loader2, Copy } from "lucide-react";

export const SignLeaseDialog = ({ signLease, setSignLease, signature, setSignature, busy, doSign, userName }) => (
  <Dialog open={!!signLease} onOpenChange={(o) => !o && setSignLease(null)}>
    <DialogContent className="bg-white rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-heading">Residential Lease Agreement</DialogTitle></DialogHeader>
      {signLease && (
        <div className="bg-white">
          <div className="border border-line rounded-lg p-6 font-body">
            <p className="text-sm text-mute">This agreement is made between <span className="font-medium text-ink">{signLease.landlord_name}</span> (Landlord) and <span className="font-medium text-ink">{userName}</span> (Tenant).</p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <p><span className="text-mute">Property:</span> {signLease.property_title}</p>
              <p><span className="text-mute">Term:</span> {signLease.start_date} → {signLease.end_date}</p>
              <p><span className="text-mute">Monthly rent:</span> {money(signLease.monthly_rent)}</p>
              <p><span className="text-mute">Security deposit:</span> {money(signLease.security_deposit)}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-line text-sm text-ink whitespace-pre-wrap leading-relaxed">{signLease.terms}</div>
            {signLease.document && (
              <div className="mt-4 pt-4 border-t border-line">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-ink flex items-center gap-1.5"><FileText className="h-4 w-4 text-forest" /> Attached lease document</p>
                  <a data-testid="lease-doc-download" href={signLease.document} download={signLease.document_name || "lease"} className="text-sm text-forest hover:underline">Download</a>
                </div>
                {signLease.document.startsWith("data:application/pdf") ? (
                  <iframe data-testid="lease-doc-viewer" title="Lease document" src={signLease.document} className="w-full h-72 rounded-lg border border-line" />
                ) : signLease.document.startsWith("data:image") ? (
                  <img data-testid="lease-doc-viewer" src={signLease.document} alt="Lease document" className="w-full rounded-lg border border-line" />
                ) : (
                  <a href={signLease.document} download={signLease.document_name || "lease"} className="text-sm text-mute underline">{signLease.document_name || "Open document"}</a>
                )}
              </div>
            )}
          </div>
          <div className="mt-4">
            <Label>Type your full legal name to sign</Label>
            <Input data-testid="signature-input" value={signature} onChange={(e) => setSignature(e.target.value)} className="mt-1.5 border-line" style={{ fontFamily: "'Outfit', cursive" }} placeholder="Your full name" />
          </div>
        </div>
      )}
      <DialogFooter>
        <button data-testid="confirm-sign-btn" disabled={busy || !signature.trim()} onClick={doSign} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign Agreement
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SignDocDialog = ({ signDoc, setSignDoc, signature, setSignature, busy, doSignDoc }) => (
  <Dialog open={!!signDoc} onOpenChange={(o) => !o && setSignDoc(null)}>
    <DialogContent className="bg-white rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-heading">Review & sign document</DialogTitle></DialogHeader>
      {signDoc && (
        <div>
          <p className="text-sm text-ink font-medium flex items-center gap-1.5"><FileText className="h-4 w-4 text-forest" /> {signDoc.doc.name}</p>
          <iframe data-testid="doc-viewer" title="Document" src={fileUrl(signDoc.doc.path)} className="mt-2 w-full h-80 rounded-lg border border-line" />
          <div className="mt-4">
            <Label>Type your full legal name to sign this document</Label>
            <Input data-testid="doc-signature-input" value={signature} onChange={(e) => setSignature(e.target.value)} className="mt-1.5 border-line" style={{ fontFamily: "'Outfit', cursive" }} placeholder="Your full name" />
            <p className="text-[11px] text-mute mt-1">Your typed name is a legally binding electronic signature applied to this document.</p>
          </div>
        </div>
      )}
      <DialogFooter>
        <button data-testid="confirm-sign-doc-btn" disabled={busy || !signature.trim()} onClick={doSignDoc} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign this document
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const CompleteAppDialog = ({
  formApp, setFormApp, formData, setFormData, formAnswers, setFormAnswers,
  lastAnswers, useLastAnswers, busy, submitForm,
}) => (
  <Dialog open={!!formApp} onOpenChange={(o) => !o && setFormApp(null)}>
    <DialogContent className="bg-white rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="complete-app-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">Complete your application</DialogTitle>
        <DialogDescription>Fill in the details below and submit your application to the landlord for review.</DialogDescription>
      </DialogHeader>
      {formApp && (
        <div className="space-y-3">
          {lastAnswers && (
            <button data-testid="use-last-answers" onClick={useLastAnswers} className="w-full border border-forest text-forest rounded-lg px-4 py-2 text-sm font-medium hover:bg-forest/5 flex items-center justify-center gap-1.5"><Copy className="h-4 w-4" /> Duplicate my last application</button>
          )}
          <div><Label>Full name <span className="text-red-500">*</span></Label><Input data-testid="cf-full-name" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="mt-1 border-line" /></div>
          <div>
            <Label htmlFor="cf-movein">Desired move-in date <span className="text-red-500">*</span></Label>
            <Input
              id="cf-movein"
              data-testid="cf-movein"
              type="date"
              value={formData.move_in_date || ""}
              min={format(new Date(), "yyyy-MM-dd")}
              onChange={(e) => setFormData({ ...formData, move_in_date: e.target.value })}
              className="mt-1 border-line"
            />
          </div>
          <div><Label>Current employer <span className="text-red-500">*</span></Label><Input data-testid="cf-employer" value={formData.employer} onChange={(e) => setFormData({ ...formData, employer: e.target.value })} className="mt-1 border-line" /></div>
          <div><Label>Employer address <span className="text-red-500">*</span></Label><Input data-testid="cf-employer-address" value={formData.employer_address} onChange={(e) => setFormData({ ...formData, employer_address: e.target.value })} className="mt-1 border-line" /></div>
          <div><Label>Supervisor name <span className="text-red-500">*</span></Label><Input data-testid="cf-supervisor-name" value={formData.supervisor_name} onChange={(e) => setFormData({ ...formData, supervisor_name: e.target.value })} className="mt-1 border-line" /></div>
          <div><Label>Supervisor phone <span className="text-red-500">*</span></Label><Input data-testid="cf-supervisor-phone" type="tel" value={formData.supervisor_phone} onChange={(e) => setFormData({ ...formData, supervisor_phone: e.target.value })} className="mt-1 border-line" /></div>
          <div><Label>Annual income (USD) <span className="text-red-500">*</span></Label><Input data-testid="cf-income" type="number" value={formData.annual_income} onChange={(e) => setFormData({ ...formData, annual_income: e.target.value })} className="mt-1 border-line" /></div>
          <div><Label>Monthly income (USD) <span className="text-red-500">*</span></Label><Input data-testid="cf-monthly-income" type="number" value={formData.monthly_income} onChange={(e) => setFormData({ ...formData, monthly_income: e.target.value })} className="mt-1 border-line" /></div>
          {(formApp.template_questions || []).map((q) => (
            <div key={q.id} data-testid={`cf-q-${q.id}`}>
              <Label>{q.label}{q.required && <span className="text-red-500"> *</span>}</Label>
              {q.type === "long_text" ? (
                <Textarea value={formAnswers[q.id] ?? ""} onChange={(e) => setFormAnswers({ ...formAnswers, [q.id]: e.target.value })} className="mt-1 border-line" />
              ) : q.type === "yes_no" ? (
                <select value={formAnswers[q.id] ?? ""} onChange={(e) => setFormAnswers({ ...formAnswers, [q.id]: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                  <option value="">Select…</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              ) : q.type === "dropdown" ? (
                <select value={formAnswers[q.id] ?? ""} onChange={(e) => setFormAnswers({ ...formAnswers, [q.id]: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                  <option value="">Select…</option>{(q.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input type={q.type === "number" ? "number" : q.type === "date" ? "date" : "text"} value={formAnswers[q.id] ?? ""} onChange={(e) => setFormAnswers({ ...formAnswers, [q.id]: e.target.value })} className="mt-1 border-line" />
              )}
            </div>
          ))}
          <div><Label>Notes (optional)</Label><Textarea data-testid="cf-notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="mt-1 border-line" /></div>
        </div>
      )}
      <DialogFooter>
        <button data-testid="cf-submit" disabled={busy} onClick={submitForm} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit application
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
